import {
  Info,
  TriangleAlert,
  CircleAlert,
  CircleCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { FLOATING_SURFACE, GLASS_SURFACE } from "../../lib/surfaces.js";
import { SHAPE_CLASS, type Shape } from "../../lib/shape.js";

/* Alert's class maps, split out of Alert.tsx so the component stays under 200
   lines (invariant 6). The API notes live on the component in Alert.tsx.
   Colour comes from lib/tones.ts, the floating paint from lib/surfaces.ts and
   the floating corner from lib/shape.ts; this file writes no hue, glass, blur
   or shadow class of its own. */

/** Where and how an Alert sits: see `AlertProps.variant`. */
export type AlertVariant = "default" | "inline" | "banner" | "floating";

/** The paint of a floating Alert (design-owner decision 7: both stay). */
export type AlertSurface = "default" | "glass";

/** The corner of a floating Alert: the shared C7 words (lib/shape.ts). */
export type AlertShape = Extract<Shape, "pill" | "rounded">;

/* Every class here is a literal so Tailwind sees it. `default` is today's
   root, unchanged. */
const VARIANT_LAYOUT: Record<AlertVariant, string> = {
  default: "flex items-start gap-3 rounded-md border px-3 py-2.5",
  banner: "flex w-full items-start gap-3 border-y px-4 py-2.5",
  inline: "flex items-start gap-1.5 text-dense-1",
  floating: "flex items-center gap-2 px-3 py-1.5 text-xs text-pb-text",
};

/* A box (default, banner) takes the tone's soft fill and border colour, and
   its copy stays pb-text, so cn() drops the map's tone text: the later class
   wins. Inline takes the tone's text colour and nothing else. Floating takes
   no tone paint: its surface paints it, and the tone shows in the glyph. */
const boxTone = (tone: StatusTone) => cn(TONES[tone].soft, "text-pb-text");

const TONE_PAINT: Record<AlertVariant, (tone: StatusTone) => string> = {
  default: boxTone,
  banner: boxTone,
  inline: (tone) => TONES[tone].text,
  floating: () => "",
};

const SURFACE_PAINT: Record<AlertSurface, string> = {
  default: FLOATING_SURFACE,
  glass: GLASS_SURFACE,
};

export interface AlertVariantsOptions {
  variant?: AlertVariant | undefined;
  tone?: StatusTone | undefined;
  surface?: AlertSurface | undefined;
  shape?: AlertShape | undefined;
}

/* The root's classes. With the defaults it is today's string, byte for byte.
   `pb-motion-banner` (ADR-0091): a notice arrives on the grammar's clock from
   @starting-style, so the commonest spelling in the app — `{error && <Alert/>}`
   — animates in with nothing changing at the call site. Pass `open` to have
   the Alert own its exit as well. */
export function alertVariants({
  variant = "default",
  tone = "info",
  surface = "default",
  shape = "pill",
}: AlertVariantsOptions = {}): string {
  return cn(
    "pb-motion-banner",
    VARIANT_LAYOUT[variant],
    TONE_PAINT[variant](tone),
    variant === "floating" && [SURFACE_PAINT[surface], SHAPE_CLASS[shape]],
  );
}

export interface AlertPartClasses {
  /** The wrapper around the glyph or the caller's `icon`. */
  readonly iconWrap: string;
  /** The glyph's size; its colour is the tone map's text entry. */
  readonly glyph: string;
  /** The column that holds the title and the body. */
  readonly content: string;
  readonly title: string;
  /** The body (children). Empty: it inherits the root's type and colour. */
  readonly body: string;
}

const BOX_PARTS: AlertPartClasses = {
  iconWrap: "mt-0.5 shrink-0",
  glyph: "w-4 h-4",
  content: "min-w-0 flex-1",
  title: "text-sm font-medium text-pb-text mb-0.5",
  body: "text-xs text-pb-text-secondary leading-relaxed",
};

export const ALERT_PARTS: Record<AlertVariant, AlertPartClasses> = {
  default: BOX_PARTS,
  banner: BOX_PARTS,
  /* A 14px glyph centred on the 16px text-dense-1 line; the title and the
     body take the root's size and tone colour. */
  inline: {
    iconWrap: "flex h-4 shrink-0 items-center",
    glyph: "w-3.5 h-3.5",
    content: "min-w-0 flex-1",
    title: "font-medium",
    body: "",
  },
  /* One line where it fits: the title leads the sentence, as on today's
     floating notice, and wraps with the body when it does not. */
  floating: {
    iconWrap: "flex shrink-0",
    glyph: "w-4 h-4",
    content: "flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1",
    title: "font-medium",
    body: "",
  },
};

/* The glyph per tone; its colour is the tone map's text entry. `ai` is Player
   AI only (ADR-0076). */
export const ALERT_GLYPH: Record<StatusTone, LucideIcon> = {
  info: Info,
  neutral: Info,
  healthy: CircleCheck,
  watch: TriangleAlert,
  critical: CircleAlert,
  ai: Sparkles,
};
